import React, { useEffect, useRef } from 'react';

/**
 * ClickToTweet — wraps article content and auto-attaches "Tweet this" buttons
 * to every <blockquote> after the content is rendered.
 *
 * Why a wrapper instead of a parser? PostDetail uses dangerouslySetInnerHTML,
 * so we let React render the article first, then walk the DOM to upgrade
 * blockquotes. Idempotent.
 */
const SITE_URL = 'https://riddhi-responsibleai-corner.org';
const TWITTER_HANDLE = 'riddhitikoo';

const buildTweetUrl = ({ text, slug }) => {
  const url = `${SITE_URL}/post/${slug}`;
  const tweet = `"${text.trim()}" — via @${TWITTER_HANDLE}\n\n${url}`;
  return `https://twitter.com/intent/tweet?text=${encodeURIComponent(tweet)}`;
};

/**
 * Upgrade every blockquote inside the given root element with a Tweet-this button.
 */
const upgradeBlockquotes = (root, slug) => {
  if (!root) return;
  const quotes = root.querySelectorAll('blockquote:not([data-tweetable-upgraded])');
  quotes.forEach((q) => {
    const text = q.textContent.trim();
    if (!text || text.length < 30 || text.length > 260) {
      // Too short for a memorable tweet, or too long to fit a quote.
      q.setAttribute('data-tweetable-upgraded', 'skip');
      return;
    }
    q.setAttribute('data-tweetable-upgraded', '1');
    q.style.position = q.style.position || 'relative';

    const a = document.createElement('a');
    a.href = buildTweetUrl({ text, slug });
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    a.setAttribute('data-testid', 'click-to-tweet');
    a.title = 'Tweet this';
    a.style.cssText = [
      'display:inline-flex','align-items:center','gap:6px',
      'margin-top:12px','padding:6px 12px','border-radius:9999px',
      'font-size:12px','font-weight:600',
      'background:#000','color:#fff','text-decoration:none',
      'transition:transform .15s, background .15s',
    ].join(';');
    a.innerHTML = `
      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
      <span>Tweet this</span>
    `;
    a.addEventListener('mouseenter', () => { a.style.background = '#1f1f1f'; });
    a.addEventListener('mouseleave', () => { a.style.background = '#000'; });

    // Wrap blockquote with figure-like styling so the button sits under it
    const wrap = document.createElement('div');
    wrap.style.cssText = 'display:block;margin-bottom:1.5rem';
    q.parentNode.insertBefore(wrap, q);
    wrap.appendChild(q);
    wrap.appendChild(a);
  });
};

export const ClickToTweet = ({ children, slug }) => {
  const ref = useRef(null);
  useEffect(() => {
    upgradeBlockquotes(ref.current, slug);
  });
  return (
    <div ref={ref} data-testid="click-to-tweet-root">
      {children}
    </div>
  );
};

export default ClickToTweet;
